import { Component, Input } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import {
  CtBinaryOperator,
  CTDatatablePaginationParameter,
  CtModelConfiguration,
  CTModelDatatableFilter,
  CtModelDatatableOperators,
  CtModelRouteData,
  CtModelService,
  CtModelType,
  CtSortOrderDescriptorParameter,
  SnackbarService
} from "@ctsolution/ct-framework";
import { CtWebapiGenericResponse, CtWebapiService, DataRequest } from "@ctsolution/ct-webapi";
import { ContactPlaceRouteData } from "../../../_core/route-data/contact-place.route-data";

/**
 * Component per la gestione dei luoghi/sedi del contact
 * (indirizzo legale, sedi operative, magazzini, ecc.)
 * 
 * Responsabilità:
 * - Carica la lista dei places associati al contact
 * - Permette l'aggiunta di nuovi places
 * - Gestisce edit e delete dei places
 * - Supporta il submit sincronizzato tramite submitAllPlaces()
 * 
 * Pattern utilizzato:
 * - Array di configurazioni (una per ogni place)
 * - Configuration-driven UI tramite template JSON
 * - Observable pattern per submit sincronizzato con forkJoin
 */
@Component({
  selector: 'app-contact-place-list',
  templateUrl: './contact-place-list.component.html',
  styleUrls: ['./contact-place-list.component.scss']
})
export class ContactPlaceListComponent {

  /** Configurazione master del contact (contiene l'OID del contact) */
  @Input() masterConfiguration: CtModelConfiguration<any> | null = null;
  
  /** Riferimento al componente parent ContactEditComponent per submit sincronizzato */
  @Input() parentComponent: any;
  
  /** Array di configurazioni per ogni place */
  configurations: Array<CtModelConfiguration<any>> = [];

  constructor(
    private _webapi: CtWebapiService,
    private _model: CtModelService<any>,
    private _snackbar: SnackbarService
  ) {}

  ngOnInit() {
    
    // Carica i places solo se siamo in modalità edit
    if (this.masterConfiguration?.RouteData?.id) {
      this.setup();
    }
  }

  /**
   * Carica la lista dei places dal backend e crea le configurazioni
   */
  setup() {
    
    const body: CtModelDatatableOperators = CtModelDatatableOperators
      .create()
      .setFilters([
        CTModelDatatableFilter
          .create()
          .setField('Contact.Oid')
          .setValue(this.masterConfiguration?.RouteData?.id)
          .setOperatorType(CtBinaryOperator.Equal)
      ])
      .setPagination(
        new CTDatatablePaginationParameter()
          .setSortOrders([
            CtSortOrderDescriptorParameter.create('Oid')
          ])
      );

    const request: DataRequest = new DataRequest()
      .setController([ContactPlaceRouteData().controller!])
      .setAction('List')
      .setQueryParams({ hideSpinner: true })
      .setBody(body);

    this._webapi
      .post<CtModelConfiguration<any>>(request)
      .subscribe((result: CtWebapiGenericResponse<CtModelConfiguration<any>>) => {

        this.configurations = new Array<CtModelConfiguration<any>>();

        // Se non ci sono places, non aggiungiamo automaticamente uno nuovo
        // L'utente può aggiungerlo manualmente se necessario
        if (!(result.Result?.DataSource ?? []).length) {
          return;
        }

        // Crea una configurazione per ogni place esistente
        result
          .Result
          .DataSource
          .forEach((ds: any) => {

            // IMPORTANTE: Trasforma il campo PlaceType da oggetto {Oid: value} a numero
            // Il form si aspetta "PlaceType": 5 invece di "PlaceType": {"Oid": 5}
            if (ds.PlaceType && typeof ds.PlaceType === 'object' && ds.PlaceType.Oid !== undefined) {
              ds.PlaceType = ds.PlaceType.Oid;
            }

            const configuration: CtModelConfiguration<any> = this.getDefaultConfiguration(ds.Oid)
              .setDataSource(ds);

            this.configurations.push(configuration);

          });

      });
  }

  /**
   * Aggiunge una nuova configurazione place
   */
  addNewConfiguration() {
    
    this.configurations.push(this.getDefaultConfiguration());
  }

  /**
   * Crea la route data per il template JSON del place
   * @returns CtModelRouteData configurato per il template JSON
   */
  private getDefaultRouteData = () => CtModelRouteData
    .create()
    .setModelType(CtModelType.FORM)
    .setController(`${window.location.origin}/assets/demo/contact-place-model.json`);

  /**
   * Crea la configurazione per le chiamate API al backend
   * @returns CtModelConfiguration per il controller ContactPlace
   */
  private getContactPlaceConfiguration = () => CtModelConfiguration
    .create()
    .setRouteData(
      CtModelRouteData
        .create()
        .setController(ContactPlaceRouteData().controller)
    );

  /**
   * Crea la configurazione di default per un place
   * @param id OID del place (se esistente)
   * @returns CtModelConfiguration configurato con i callback onCreate e onDelete
   */
  private getDefaultConfiguration = (id: number | null = null) => CtModelConfiguration
    .create()
    .setRouteData(this.getDefaultRouteData())
    .setDefaultActionsEnabled(false)
    .setOnCreate((event: any) => {

      // Controlla se siamo in modalità edit verificando se esiste un id nel masterConfiguration
      const isEditMode = this.masterConfiguration?.RouteData?.id;
      
      // Se abbiamo il parent component, siamo in edit mode E il metodo submitAll esiste, usa il submit sincronizzato
      if (this.parentComponent && this.parentComponent.submitAll && isEditMode) {
        this.parentComponent.submitAll();
      } else {
        // Altrimenti usa il metodo tradizionale standalone
        const configuration: CtModelConfiguration<any> = this.getContactPlaceConfiguration();

        if (id) {
          configuration.RouteData?.setId(id);
        }

        // Imposta il ContactOid
        event.ContactOid = this.masterConfiguration?.RouteData?.id ?? -1;

        // IMPORTANTE: Trasforma il campo PlaceType da numero a oggetto {Oid: value}
        // Il backend si aspetta "PlaceType": {"Oid": 5} invece di "PlaceType": 5
        if (event.PlaceType && typeof event.PlaceType === 'number') {
          event.PlaceType = { Oid: event.PlaceType };
        }

        this._model
          .putInfos(configuration, event)
          ?.subscribe(() => {
            this._snackbar.onSuccess();
            this.setup();  // Ricarica la lista
          });
      }

    })
    .setOnDelete((value: { idx: number }) => {

      // Se non c'è un id, rimuovi semplicemente dalla lista
      if (!id) {
        this.configurations.pop();
        return;
      }

      // Altrimenti, chiama l'API per cancellare
      const configuration: CtModelConfiguration<any> = this.getContactPlaceConfiguration();

      configuration.RouteData?.setId(id);

      const controller: string | undefined | null = configuration.RouteData?.controller;

      if (!controller) return;

      const request: DataRequest = new DataRequest()
        .setController([controller, 'Delete'])
        .setAction(id.toString());

      this._webapi
        .delete(request)
        .subscribe(() => {
          this._snackbar.onSuccess();
          this.setup();  // Ricarica la lista
        });

    });

  /**
   * Metodo esposto per il submit sincronizzato dal parent component
   * Salva tutti i places in parallelo
   * @returns Observable<any>
   */
  submitAllPlaces(): Observable<any> {
    
    // Se non ci sono configurazioni (places), ritorna un Observable vuoto con successo
    if (!this.configurations || this.configurations.length === 0) {
      return of({ success: true, message: 'No places to save' });
    }

    // Array di Observable per ogni place da salvare
    const saveObservables: Observable<any>[] = [];

    this.configurations.forEach((config, index) => {
      
      // Se il form non esiste o non è valido, skippo (la validazione è già stata fatta dal parent)
      if (!config.form) {
        return;
      }

      // Creo un Observable per salvare questo specifico place
      const saveObservable = new Observable(observer => {
        
        const placeData = config.form!.value;
        placeData.ContactOid = this.masterConfiguration?.RouteData?.id ?? -1;

        // IMPORTANTE: Trasforma il campo PlaceType da numero a oggetto {Oid: value}
        // Il backend si aspetta "PlaceType": {"Oid": 5} invece di "PlaceType": 5
        if (placeData.PlaceType && typeof placeData.PlaceType === 'number') {
          placeData.PlaceType = { Oid: placeData.PlaceType };
        }

        const configuration: CtModelConfiguration<any> = this.getContactPlaceConfiguration();

        // Se il place ha già un Oid (esiste nel datasource), lo setto per fare update
        const placeOid = config.DataSource?.Oid;
        if (placeOid) {
          configuration.RouteData?.setId(placeOid);
        }

        this._model
          .putInfos(configuration, placeData)
          ?.subscribe({
            next: (response) => {
              observer.next(response);
              observer.complete();
            },
            error: (err) => {
              console.error(`Errore durante il salvataggio del place ${index + 1}:`, err);
              observer.error(err);
            }
          });
      });

      saveObservables.push(saveObservable);
    });

    // Se non ci sono places da salvare, ritorna successo
    if (saveObservables.length === 0) {
      return of({ success: true, message: 'No places to save' });
    }

    // Eseguo tutti i salvataggi in parallelo con forkJoin
    return forkJoin(saveObservables);
  }

}
